import { MonthlyBucket, MonthlyCharge, MonthlyRuleId, PricingMonthlyRow } from './api';
import { effectiveMonthlyCaseNames } from './monthlyCaseScope';
import { MONTHLY_POLICY, MONTHLY_RULES, simpleMonthlyLines } from './monthlySimpleMath';
import { monthlyTariffOptions } from './monthlyTariffs';

const buckets: MonthlyBucket[] = ['communication', 'processing', 'infrastructure', 'third_party'];
const catalog = buckets.flatMap(monthlyTariffOptions);
const tariff = (ruleId: MonthlyRuleId) => {
  const option = catalog.find(value => value.value === ruleId);
  if (!option) throw new Error('Choose a standard monthly tariff.');
  return option;
};

/** Mirrors save-time coverage limits before a tariff is offered or applied. */
export function monthlyTariffAvailability(
  rows: PricingMonthlyRow[], ruleId: MonthlyRuleId,
  replace?: { row: number; charge: number }, addToRow?: number | null,
): string | null {
  const option = catalog.find(value => value.value === ruleId);
  if (!option) return 'Choose a standard monthly tariff.';
  const bucket = MONTHLY_RULES[ruleId].bucket;
  if (rows.length > 40) return 'Monthly pricing supports up to 40 service rows.';
  if (replace) {
    const row = rows[replace.row];
    if (!Number.isInteger(replace.row) || !Number.isInteger(replace.charge) || replace.row < 0 || !row
        || (row.model === 'simple' ? replace.charge < 0 || !row.simple?.charges[replace.charge] : replace.charge !== 0 && replace.charge !== -1)) {
      return 'Choose the monthly charge to replace.';
    }
    if (row.model === 'simple' && row.simple && row.simple.charges.length > 1 && row.simple.bucket !== bucket) {
      return 'Choose a tariff from the same service category.';
    }
  }
  const remaining = rows.flatMap((row, rowIndex) => row.model === 'simple' ? (row.simple?.charges || [])
    .filter((_charge, chargeIndex) => !replace || replace.row !== rowIndex || replace.charge !== chargeIndex) : []);
  if ((ruleId === 'sms_email' && remaining.some(charge => charge.ruleId === 'sms' || charge.ruleId === 'email'))
      || ((ruleId === 'sms' || ruleId === 'email') && remaining.some(charge => charge.ruleId === 'sms_email'))) {
    return 'Use SMS + email or separate SMS and email tariffs, not both.';
  }
  const infrastructure = (value: MonthlyRuleId) => value.startsWith('platform_') || value.startsWith('hosting_');
  if (infrastructure(ruleId) && remaining.some(charge => infrastructure(charge.ruleId))) {
    return 'A platform or hosting fee is already included.';
  }
  if (!replace) {
    const target = addToRow === undefined ? rows.findIndex(row => row.model === 'simple' && row.simple?.bucket === bucket
      && !row.simple.charges.some(charge => charge.ruleId === 'phase_addon' || charge.ruleId === 'phase_user')) : addToRow;
    if (target !== null && target !== -1) {
      const row = rows[target];
      if (!Number.isInteger(target) || target < 0 || row?.model !== 'simple' || row.simple?.bucket !== bucket) {
        return 'Choose a matching service category.';
      }
      if (row.simple.charges.length >= 12) return 'This service already has 12 tariffs.';
    } else if (rows.length >= 40) return 'Monthly pricing supports up to 40 service rows.';
  }
  return null;
}

/** Defaults never invent a client volume, agreed call minimum or external cost. */
export function createStandardCharge(ruleId: MonthlyRuleId, id = `manual-${ruleId}`): MonthlyCharge {
  const option = tariff(ruleId);
  const variable = !option.fixed && ruleId !== 'third_party';
  return {
    id, ruleId, label: option.label,
    evidence: { source: 'manual-review', quote: 'Tariff selected by reviewer' },
    ...(variable ? { quantity: null } : {}),
    ...(variable && ruleId !== 'payroll' ? { minimum: ruleId === 'voice' ? null : 0 } : {}),
    ...(ruleId === 'third_party' || ruleId === 'third_party_record' ? { unitCost: null } : {}),
  };
}

/** Only these tier families retain the same measured usage when their rate changes. */
function pricingBasis(ruleId: MonthlyRuleId): string {
  if (ruleId === 'platform_500' || ruleId === 'platform_750' || ruleId === 'platform_850') return 'platform-monthly';
  if (ruleId === 'hosting_500' || ruleId === 'hosting_750') return 'hosting-monthly';
  if (ruleId === 'processing_heavy' || ruleId === 'processing_light') return 'processing-active-unit';
  return ruleId;
}

const replaceCharge = (charge: MonthlyCharge, ruleId: MonthlyRuleId): MonthlyCharge => {
  tariff(ruleId);
  if (pricingBasis(charge.ruleId) === pricingBasis(ruleId)) return { ...charge, ruleId };
  const retained = { ...charge };
  delete retained.quantity;
  delete retained.minimum;
  delete retained.unitCost;
  delete retained.manualRate;
  delete retained.unitLabel;
  const fresh = createStandardCharge(ruleId, charge.id);
  return { ...retained, ruleId, ...('quantity' in fresh ? { quantity: fresh.quantity } : {}),
    ...('minimum' in fresh ? { minimum: fresh.minimum } : {}),
    ...('unitCost' in fresh ? { unitCost: fresh.unitCost } : {}) };
};

// Stable default identity for an explicit legacy conversion. Callers adding
// repeated identical services can pass their own unique newChargeId.
function convertedChargeId(row: PricingMonthlyRow, ruleId: MonthlyRuleId): string {
  const identity = JSON.stringify([row.label, row.case ?? null, row.caseNames ?? null, row.model ?? null]);
  let hash = 2166136261;
  for (let index = 0; index < identity.length; index += 1) hash = Math.imul(hash ^ identity.charCodeAt(index), 16777619);
  return `manual-${ruleId}-${(hash >>> 0).toString(36)}`;
}

/** Builds a candidate only. The caller applies it after an explicit Use tariff action. */
export function convertMonthlyTariff(row: PricingMonthlyRow, ruleId: MonthlyRuleId, chargeIndex = 0, newChargeId?: string): PricingMonthlyRow {
  tariff(ruleId);
  const bucket = MONTHLY_RULES[ruleId].bucket;
  if (row.model === 'simple') {
    if (!row.simple || !Number.isInteger(chargeIndex) || chargeIndex < 0 || !row.simple.charges[chargeIndex]) {
      throw new Error('Choose the monthly charge to replace.');
    }
    if (row.simple.charges.length > 1 && bucket !== row.simple.bucket) {
      throw new Error('Choose a tariff from the same service category.');
    }
    const simple = { ...row.simple, bucket, charges: row.simple.charges.map((charge, index) =>
      index === chargeIndex ? replaceCharge(charge, ruleId) : charge) };
    return { ...row, simple, price: simpleMonthlyLines(simple).price, overage: null };
  }
  if (chargeIndex !== 0) throw new Error('Choose the monthly charge to replace.');
  const scope = effectiveMonthlyCaseNames(row);
  const charge = createStandardCharge(ruleId, newChargeId ?? convertedChargeId(row, ruleId));
  const label = (row.displayLabel ?? row.label).trim() ? row.displayLabel ?? row.label : charge.label;
  const description = row.displayDescription ?? row.description;
  const simple = { policyVersion: MONTHLY_POLICY, bucket, charges: [{ ...charge,
    ...(label.trim() ? { label } : {}), ...(scope !== undefined ? { caseNames: scope } : {}),
  }] };
  // Old numeric/text terms cannot carry into a different pricing agreement.
  // Explicit undefined also clears these keys when the parent merges this
  // candidate as a shallow patch; JSON persistence then omits the old terms.
  return { ...row, amount: undefined, rate: undefined, minimum: undefined,
    unit: undefined, unit_label: undefined, label, model: 'simple', simple,
    ...(label.trim() ? { displayLabel: label } : {}),
    ...(description !== undefined ? { displayDescription: description } : {}),
    price: simpleMonthlyLines(simple).price, overage: null };
}
